<template>
  <div class="tabs-component">
    <div class="tabs-component--header">
      <ul role="tablist" class="tabs-component--tabs">
        <li
          v-for="tab in tabs"
          :key="tab.id"
          :class="{'is-active': tab.isActive, 'has-error': hasError}"
          class="tabs-component--tab"
          role="presentation"
        >
          <Button
            severity="tab"
            :class="{'button--active': activeTab === tab.id}"
            :aria-selected="activeTab === tab.id"
            :aria-controls="`tab-${tab.id}`"
            @click="selectTab(tab.id)"
            role="tab"
          >
            {{ tab.title }} <component v-if="tab.icon" :is="tab.icon" />
          </Button>
        </li>
      </ul>
      <slot name="tabHeader"></slot>
    </div>
    <div class="tabs-component-panels">
      <slot  />
    </div>
  </div>
</template>

<script setup lang="ts">
import { provide, ref, watch } from 'vue';

import Button from './Button.vue';

import type { Tab } from '@/types/Tab';

const tabs = ref<Tab[]>([]);
const activeTab = ref('');

const props = defineProps<{
  defaultTab: string;
  hasError?: boolean;
}>();

const emit = defineEmits(['tabChanged']);

const selectTab = (tab: string) => {
  activeTab.value = tab;
  emit('tabChanged', tab);
};

watch(() => props.defaultTab, (newVal) => {
  if (newVal) {
    activeTab.value = newVal;
  }
}, { immediate: true});

function registerTab(tab: Tab) {
  if (tabs.value) {
    const existingTab = tabs.value.findIndex(extTab => extTab.id === tab.id);
    if (existingTab > -1) {
      tabs.value[existingTab] = tab;
    } else {
      tabs.value.push(tab);
    }
  }
}

provide('registerTab', registerTab);
provide('activeTab', activeTab);
</script>

<style lang="scss">
.tabs-component {
  .tabs-component--tabs {
    @include mixins.flex-center($horiz: flex-start, $gap: variables.$sizing-xxs);
    list-style-type: none;
    margin: variables.$sizing-xs 0;
    margin-right: auto;
  }

  .tabs-component--header {
    @include mixins.flex-center($horiz: flex-start, $gap: variables.$sizing-xxs);
  }

  .has-error svg {
    color: variables.$primitives-color-red-100;
  }
}
</style>
